import { FC, useEffect } from 'react'
import { RenderIf } from 'components/RenderIf'
import { useGetProductPrice } from 'featureSliced/shared/hooks/useGetProductPrice/indexT'
import { Button } from 'featureSliced/entities/Button'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'
import { AmountHandler } from 'featureSliced/widgets/AmountHandler'

import { IProduct } from 'featureSliced/shared/store/reducers/CategoriesReducer/types'
import { QRMBucketSlice } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer'
import { TableStatuses } from 'featureSliced/shared/store/reducers/QRMenuTableReducer/types'

import { Divider, FooterContainer } from '../ComonProduct/styled'
import { KitchenInfoItemType } from '../types'
import { ProductTemplateIdType } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer/types'

type AddCommonProductFooterProps = {
  product: IProduct
  productTemplateId: ProductTemplateIdType
  amountState: [number, (value: number) => void]
  closeProduct: () => void
  selectedKitchenInfo: KitchenInfoItemType
  initAmount: number
}

export const AddCommonProductFooter: FC<AddCommonProductFooterProps> = ({
  product,
  productTemplateId,
  amountState,
  closeProduct,
  selectedKitchenInfo,
  initAmount,
}) => {
  // Hooks
  const t = useTranslations()
  const dispatch = useMyDispatch()
  const addProductToBucket = QRMBucketSlice.actions.addProductToBucket
  const selectedQRMProducts = useMySelector((state) => state.bucket.products)
  const removeProductFromBucket = QRMBucketSlice.actions.removeProductFromBucket
  const { getProductPrice } = useGetProductPrice()

  // Store
  const tableStatus = useMySelector((state) => state.qrMenuTable.tableStatus)
  const bookingVariant = useMySelector((state) => state.app.menu.bookingVariant)
  const disabledByMobileWaiter = useMySelector(
    (state) => state.app.menu.disabledByMobileWaiter
  )

  // State
  const [currentAmount, setCurrentAmount] = amountState

  const getMultiplier = (sellCode: number | null) => {
    if (sellCode === 2) {
      return 100
    } else {
      return 1
    }
  }

  const getStep = (sellCode: number | null) => {
    if (sellCode === 3) {
      return 0.1
    } else {
      return 1
    }
  }

  const uniqueId = `${productTemplateId}_${product.id}_${JSON.stringify(
    selectedKitchenInfo
  )}`
  const productFromBucket = selectedQRMProducts?.find(
    (item) => item.uniqueId === uniqueId
  )
  const isProductInBucket = Boolean(productFromBucket)
  const stepValue = getStep(product.unitSellCode)
  const clickCollectMin = product.ClickCollectOrderMin
  const hasClickCollectMin =
    clickCollectMin !== null && clickCollectMin !== undefined
  const defaultMinValue = hasClickCollectMin ? clickCollectMin : stepValue
  const minValue = isProductInBucket ? 0 : defaultMinValue
  const nextValueAfterZero = hasClickCollectMin ? clickCollectMin : stepValue

  // useEffect
  useEffect(() => {
    if (initAmount) {
      setCurrentAmount(initAmount)
    }
  }, [initAmount])

  useEffect(() => {
    if (!isProductInBucket && currentAmount < defaultMinValue) {
      setCurrentAmount(defaultMinValue)
    }
  }, [isProductInBucket, currentAmount, defaultMinValue, setCurrentAmount])

  const addToBucket = () => {
    const { id, categoryId, unitSellCode } = product
    const productToAdd = {
      uniqueId,
      productId: id,
      productTemplateId,
      categoryId,
      count: currentAmount,
      kitchenInfo: JSON.stringify(selectedKitchenInfo || {}),
      price: getProductPrice(product, productTemplateId).finalPrice,
      addedAsWeight: unitSellCode === 2 || unitSellCode === 3,
    }
    if (currentAmount) {
      dispatch(addProductToBucket(productToAdd))
    } else {
      dispatch(removeProductFromBucket(uniqueId))
    }
    closeProduct()
  }

  const handleDecreaseAmount = (value: number) => {
    setCurrentAmount(value)
  }

  const handleIncreaseAmount = (value: number) => {
    if (currentAmount === 0) {
      if (isProductInBucket) {
        setCurrentAmount(nextValueAfterZero)
        return
      }
      if (!isProductInBucket && currentAmount < defaultMinValue) {
        setCurrentAmount(defaultMinValue)
        return
      }
    }
    setCurrentAmount(value)
  }

  return (
    <RenderIf
      key={`${navigator.onLine}${bookingVariant}${disabledByMobileWaiter}${tableStatus}${product.soldOut}`}
      condition={
        navigator.onLine &&
        (bookingVariant === 2 || process.env.REACT_APP_IS_CLIC === '1') &&
        !disabledByMobileWaiter &&
        tableStatus !== TableStatuses.BUSY &&
        !product.soldOut
      }
    >
      <Divider />
      <FooterContainer>
        <AmountHandler
          multiplier={getMultiplier(product.unitSellCode)}
          step={stepValue}
          currentValue={currentAmount}
          onDecrease={handleDecreaseAmount}
          onIncrease={handleIncreaseAmount}
          minValue={minValue} // only needed for C&C
          maxValue={product.ClickCollectOrderMax ?? undefined} // only needed for C&C
        />
        <Button
          buttonType="common"
          disabled={tableStatus === TableStatuses.BUSY}
          width="100%"
          onClick={addToBucket}
        >
          {currentAmount ? t('products.addOrder') : t('buttons.remove')}
        </Button>
      </FooterContainer>
    </RenderIf>
  )
}
